import React, { useState, useEffect, useCallback } from "react";
import { useDesktopNav } from "./navigation/DesktopNavContext";
import { DesktopBackLink } from "./navigation/ScreenBackButton";

import { supabase } from "../supabase";
import { computeRowBaseGrossPay } from "../helpers/payrollHelpers";
import { DEFAULT_PAYROLL_REPORT_OPTIONS } from "../helpers/payrollReport";
import { PayrollReportOptionsScreen } from "./payroll/report/PayrollReportOptionsScreen";
import { PayrollReportPreviewScreen } from "./payroll/report/PayrollReportPreviewScreen";
import { usePermissions } from "../hooks/usePermissions";
import { useOrganization } from "../context/OrganizationContext";
import { CAPABILITY, DATA_SCOPE } from "../helpers/permissions";
import {
  applyOperationalTimeLogOrgScope,
  applyUserIdsToQuery,
  loadScopedUsers,
  PAYROLL_SCOPE_SELECTION,
  resolveOperationalOrganizationId,
  resolvePayrollSelectionUserIdsForQuery,
  selectOperationalTimeLogs,
} from "../helpers/scopedQueries";
import { blockDemoSave, shouldSkipSupabase } from "../helpers/guestMode";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { CPDropdown } from "./CPDropdown";
import { CPInput } from "./CPInput";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { PayrollReportFormDesktop } from "./payroll/PayrollDesktop";

function mapReportScopeToPayrollSelection(reportScope, selectedEmployee) {
  if (reportScope === "entire_team") {
    return PAYROLL_SCOPE_SELECTION.MY_CREW;
  }

  if (reportScope === "entire_organization") {
    return PAYROLL_SCOPE_SELECTION.ALL_EMPLOYEES;
  }

  if (reportScope === "crew_member" && selectedEmployee) {
    return selectedEmployee;
  }

  return PAYROLL_SCOPE_SELECTION.MY_TIME;
}

export function PayrollReportScreen({ onBack, currentUser }) {
  const desktopNav = useDesktopNav();
  const isDesktop = useIsDesktopViewport();
  const { scope, directReportIds } = usePermissions();
  const { settings } = useOrganization();
  const payrollScope = scope(CAPABILITY.PAYROLL_REPORT);
  const showTeamScope = payrollScope === DATA_SCOPE.TEAM;
  const showOrgScope = payrollScope === DATA_SCOPE.ORGANIZATION;
  const showCrewMemberScope = showTeamScope || showOrgScope;

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportScope, setReportScope] = useState("my_time");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [reportRows, setReportRows] = useState([]);
  const [reportMeta, setReportMeta] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [view, setView] = useState("filters");
  const [reportOptions, setReportOptions] = useState(
    DEFAULT_PAYROLL_REPORT_OPTIONS,
  );

  async function loadEmployees() {
    if (shouldSkipSupabase(currentUser)) {
      setEmployees([]);
      return;
    }

    try {
      if (!currentUser || !showCrewMemberScope) {
        setEmployees([]);
        return;
      }

      const data = await loadScopedUsers(
        supabase,
        currentUser,
        CAPABILITY.PAYROLL_REPORT,
        "supabase_user_id, employee_name, email, phone",
        { directReportIds },
      );
      setEmployees(data);
    } catch (err) {
      console.error("EMPLOYEE LOAD ERROR:", err);
      setEmployees([]);
    }
  }

  useEffect(() => {
    loadEmployees();
  }, [currentUser, payrollScope, directReportIds]);

  async function handleGenerateReport() {
    if (shouldSkipSupabase(currentUser)) {
      setReportRows([]);
      setReportMeta({
        employeeName: "Demo",
        startDate,
        endDate,
      });
      setReportOptions(DEFAULT_PAYROLL_REPORT_OPTIONS);
      setView("report-options");
      return;
    }

    try {
      setIsGenerating(true);

      const payrollSelection = mapReportScopeToPayrollSelection(
        reportScope,
        selectedEmployee,
      );

      let query = selectOperationalTimeLogs(
        supabase,
        `
  id,
  employee,
  project_name,
  date_iso,
  start_time,
  end_time,
  total_hours,
  break_seconds,
  payroll_status,
  supabase_user_id,
  classification_name,
  payroll_type,
  trade_name,
  hourly_rate
`,
      )
        .gte("date_iso", startDate)
        .lte("date_iso", endDate)
        .order("date_iso", { ascending: false });

      const payrollUserIds = await resolvePayrollSelectionUserIdsForQuery(
        supabase,
        currentUser,
        payrollSelection,
        { directReportIds },
      );
      const organizationId = await resolveOperationalOrganizationId(
        currentUser,
        supabase,
        { directReportIds },
      );

      query = applyUserIdsToQuery(query, payrollUserIds);
      query = applyOperationalTimeLogOrgScope(query, organizationId);

      const { data, error } = await query;

      if (error) throw error;

      const payrollRows = (data || []).map((row) => ({
        ...row,
        gross_pay: computeRowBaseGrossPay(row),
      }));

      const employeeScope =
        reportScope === "crew_member" ? selectedEmployee : reportScope;

      const employeeName =
        reportScope === "crew_member"
          ? (
              employees.find(
                (employee) =>
                  employee.supabase_user_id === selectedEmployee,
              ) || {}
            ).employee_name
          : currentUser.employee_name;

      setReportRows(payrollRows);
      setReportMeta({
        startDate,
        endDate,
        employeeName,
        employeeScope,
        employees,
        currentUser,
        orgSettings: settings,
      });
      setReportOptions(DEFAULT_PAYROLL_REPORT_OPTIONS);
      setView("report-options");
    } catch (err) {
      console.error("PAYROLL REPORT ERROR:", err);
      alert("Unable to generate payroll report.");
    } finally {
      setIsGenerating(false);
    }
  }

  const handleHardwareBack = useCallback(() => {
    if (view === "report-preview") {
      setView("report-options");
      return true;
    }

    if (view === "report-options") {
      setView("filters");
      return true;
    }

    return false;
  }, [view]);

  useHardwareBackHandler(handleHardwareBack);

  if (view === "report-options") {
    return (
      <PayrollReportOptionsScreen
        reportTitle={reportMeta?.employeeName}
        options={reportOptions}
        onChange={setReportOptions}
        onPreview={(mergedOptions) => {
          setReportOptions(mergedOptions);
          setView("report-preview");
        }}
        onBack={() => setView("filters")}
        parentLabel="Payroll Reports"
      />
    );
  }

  if (view === "report-preview") {
    return (
      <PayrollReportPreviewScreen
        rows={reportRows}
        meta={reportMeta}
        options={reportOptions}
        onBack={() => setView("report-options")}
        onEditOptions={() => setView("report-options")}
      />
    );
  }

  if (isDesktop) {
    return (
      <PayrollReportFormDesktop
        onBack={onBack}
        employeeName={currentUser?.employee_name}
        startDate={startDate}
        endDate={endDate}
        onStartDate={setStartDate}
        onEndDate={setEndDate}
        reportScope={reportScope}
        onScope={setReportScope}
        scopeOptions={[
          { value: "my_time", label: "My Time" },
          ...(showCrewMemberScope
            ? [
                { value: "crew_member", label: "Crew Member" },
                ...(showTeamScope ? [{ value: "entire_team", label: "Entire Team" }] : []),
              ]
            : []),
          ...(showOrgScope ? [{ value: "entire_organization", label: "Entire Organization" }] : []),
        ]}
        showEmployee={reportScope === "crew_member"}
        selectedEmployee={selectedEmployee}
        onEmployee={setSelectedEmployee}
        employees={employees}
        onGenerate={handleGenerateReport}
        isGenerating={isGenerating}
      />
    );
  }

  return (
    <div className="intel-screen">
      <div className="cockpit-header">
        {desktopNav ? (<DesktopBackLink onClick={onBack} label={desktopNav.parentLabel} />) : (
        <button className="cockpit-back-btn" onClick={onBack}>
          Back
        </button>
        )}

        <div>
          <div className="cockpit-title">PAYROLL REPORTS</div>

          <div
            style={{
              color: "var(--nx-gold)",
              marginTop: "6px",
            }}
          >
            {currentUser?.employee_name}
          </div>
        </div>
      </div>

      <div className="rb-card">
        <div className="rb-field-group">
          <CPInput
            label="Start Date"
            type="date"
            value={startDate}
            onChange={setStartDate}
          />
          {startDate && (
            <div
              style={{
                marginTop: "6px",
                color: "var(--nx-gold)",
                fontSize: "16px",
                fontWeight: "500",
              }}
            >
              {new Date(`${startDate}T12:00:00`).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          )}
        </div>

        <div className="rb-field-group">
          <CPInput
            label="End Date"
            type="date"
            value={endDate}
            onChange={setEndDate}
          />
          {endDate && (
            <div
              style={{
                marginTop: "6px",
                color: "var(--nx-gold)",
                fontSize: "16px",
                fontWeight: "500",
              }}
            >
              {new Date(`${endDate}T12:00:00`).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          )}
        </div>

        <div className="rb-field-group">
          <CPDropdown
            label="Scope"
            value={reportScope}
            onChange={setReportScope}
            includePlaceholder={false}
            options={[
              { value: "my_time", label: "My Time" },
              ...(showCrewMemberScope
                ? [
                    { value: "crew_member", label: "Crew Member" },
                    ...(showTeamScope
                      ? [
                          {
                            value: "entire_team",
                            label: "Entire Team",
                          },
                        ]
                      : []),
                  ]
                : []),
              ...(showOrgScope
                ? [
                    {
                      value: "entire_organization",
                      label: "Entire Organization",
                    },
                  ]
                : []),
            ]}
          />
        </div>

        {reportScope === "crew_member" && (
          <div className="rb-field-group">
            <CPDropdown
              label="Employee"
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              options={[
                { value: "", label: "Select Employee" },
                ...employees.map((employee) => ({
                  value: employee.supabase_user_id,
                  label: employee.employee_name,
                })),
              ]}
            />
          </div>
        )}

        <button
          className="rb-generate-btn"
          onClick={handleGenerateReport}
          disabled={!startDate || !endDate || isGenerating}
        >
          {isGenerating ? "Generating..." : "Generate Payroll Report"}
        </button>
      </div>
    </div>
  );
}
